import { useEffect, useMemo, useState } from "react";

import { env } from "../../utils/env.js";
import {
  DESKTOP_APP_NOTIFICATION_KEY,
  INTRODUCTION_VIDEO_NOTIFICATION_KEY,
} from "./notification-keys.js";
import { useNotificationSettingsStore } from "./notification-settings.store.js";

type LobbyDialog =
  | "onboarding-video"
  | "create-team"
  | "changelog"
  | "desktop-app"
  | null;
export type TeamsQueryState = "loading" | "success" | "error";

interface UseLobbyDialogParams {
  createTeamJobsState: TeamsQueryState;
  createTeamJobsBlockAutoOpen: boolean;
  teamsState: TeamsQueryState;
  teamsCount: number;
  editMode: boolean;
  changelogId: string | null;
  changelogPublishedAt: string | null;
}

export function useLobbyDialog({
  createTeamJobsState,
  createTeamJobsBlockAutoOpen,
  teamsState,
  teamsCount,
  editMode,
  changelogId,
  changelogPublishedAt,
}: UseLobbyDialogParams) {
  const {
    getNotificationAction,
    setNotificationAction,
    changelog,
    setChangelogNotification,
  } = useNotificationSettingsStore();

  // Track whether the video was dismissed this session (for transition to next dialog)
  const [videoDismissedThisSession, setVideoDismissedThisSession] =
    useState(false);

  // Track whether the user manually re-opened the video via the floating button
  const [videoManuallyOpened, setVideoManuallyOpened] = useState(false);
  // Keep create-team dismissed for the current session only
  const [createTeamDismissedThisSession, setCreateTeamDismissedThisSession] =
    useState(false);
  const [createTeamAutoOpenEligible, setCreateTeamAutoOpenEligible] = useState<
    boolean | null
  >(null);
  // Keep changelog dismissal in this session so desktop app prompt appears next visit
  const [changelogDismissedThisSession, setChangelogDismissedThisSession] =
    useState(false);
  const [desktopAppManuallyOpened, setDesktopAppManuallyOpened] =
    useState(false);

  const videoId = env.PUBLIC_VIDEO_INTRO_ID;
  const videoNotificationAction = getNotificationAction(
    INTRODUCTION_VIDEO_NOTIFICATION_KEY
  );
  const desktopAppNotificationAction = getNotificationAction(
    DESKTOP_APP_NOTIFICATION_KEY
  );

  const videoNeeded =
    Boolean(videoId) && !videoNotificationAction && !videoDismissedThisSession;

  const hasCompletedInitialIntroductionVideo = videoId
    ? Boolean(videoNotificationAction) || videoDismissedThisSession
    : true;

  const shouldShowCurrentChangelog = useMemo(() => {
    if (!(changelogId && changelogPublishedAt)) {
      return false;
    }

    if (!changelog) {
      return true;
    }

    if (changelog.id !== changelogId) {
      return new Date(changelogPublishedAt) > new Date(changelog.publishedAt);
    }

    return false;
  }, [changelog, changelogId, changelogPublishedAt]);

  const desktopAppPromptNeeded =
    hasCompletedInitialIntroductionVideo &&
    teamsState === "success" &&
    teamsCount > 0 &&
    Boolean(changelog) &&
    !changelogDismissedThisSession &&
    !desktopAppNotificationAction;

  useEffect(() => {
    if (createTeamAutoOpenEligible !== null) {
      return;
    }

    if (teamsState === "error" || createTeamJobsState === "error") {
      setCreateTeamAutoOpenEligible(false);
      return;
    }

    if (teamsState === "success" && createTeamJobsState === "success") {
      setCreateTeamAutoOpenEligible(
        !editMode && teamsCount === 0 && !createTeamJobsBlockAutoOpen
      );
    }
  }, [
    createTeamAutoOpenEligible,
    createTeamJobsBlockAutoOpen,
    createTeamJobsState,
    editMode,
    teamsCount,
    teamsState,
  ]);

  const shouldShowCreateTeamDialog =
    createTeamAutoOpenEligible === true && !createTeamDismissedThisSession;
  const shouldShowChangelogDialog =
    teamsState === "success" && teamsCount > 0 && shouldShowCurrentChangelog;

  const activeDialog: LobbyDialog = useMemo(() => {
    // If manually re-opened, force video dialog
    if (videoManuallyOpened) {
      return "onboarding-video";
    }

    if (
      desktopAppManuallyOpened &&
      desktopAppNotificationAction !== "doNotShowAgain"
    ) {
      return "desktop-app";
    }

    // Priority 1: Onboarding video
    if (videoNeeded) {
      return "onboarding-video";
    }

    // Priority 2: Create team (only when teams are loaded and empty)
    if (shouldShowCreateTeamDialog) {
      return "create-team";
    }

    // Priority 3: Changelog (only when teams are loaded and exist)
    if (shouldShowChangelogDialog) {
      return "changelog";
    }

    // Priority 4: Desktop app prompt (after changelog and first full onboarding flow)
    if (desktopAppPromptNeeded) {
      return "desktop-app";
    }

    return null;
  }, [
    videoManuallyOpened,
    desktopAppManuallyOpened,
    desktopAppNotificationAction,
    videoNeeded,
    shouldShowCreateTeamDialog,
    shouldShowChangelogDialog,
    desktopAppPromptNeeded,
  ]);

  const dismissOnboardingVideo = (action: "dismissed" | "doNotShowAgain") => {
    setNotificationAction(INTRODUCTION_VIDEO_NOTIFICATION_KEY, action);
    setVideoDismissedThisSession(true);
    setVideoManuallyOpened(false);
  };

  const requestOpenVideo = () => {
    setVideoManuallyOpened(true);
    setDesktopAppManuallyOpened(false);
  };

  const dismissCreateTeam = () => {
    setCreateTeamDismissedThisSession(true);
  };

  const dismissChangelog = () => {
    if (changelogId && changelogPublishedAt) {
      setChangelogNotification({
        id: changelogId,
        publishedAt: changelogPublishedAt,
      });
      setChangelogDismissedThisSession(true);
    }
  };

  const minimizeDesktopAppPrompt = () => {
    setNotificationAction(DESKTOP_APP_NOTIFICATION_KEY, "dismissed");
    setDesktopAppManuallyOpened(false);
  };

  const requestOpenDesktopAppPrompt = () => {
    setDesktopAppManuallyOpened(true);
    setVideoManuallyOpened(false);
  };

  // Expose whether the floating button should show
  const showVideoFloatingButton =
    Boolean(videoId) &&
    videoNotificationAction === "dismissed" &&
    activeDialog !== "onboarding-video";
  const showDesktopAppFloatingButton =
    desktopAppNotificationAction === "dismissed" &&
    activeDialog !== "desktop-app";

  return {
    activeDialog,
    dismissChangelog,
    dismissCreateTeam,
    dismissOnboardingVideo,
    minimizeDesktopAppPrompt,
    requestOpenDesktopAppPrompt,
    requestOpenVideo,
    showDesktopAppFloatingButton,
    showVideoFloatingButton,
    videoId: videoId ?? null,
  };
}
